import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useGetPendingPublicChange from '@/features/dashboard/bookingChanges/hooks/useGetPendingPublicChange.ts';
import useAcceptPublicChange from '@/features/dashboard/bookingChanges/hooks/useAcceptPublicChange.ts';
import useRejectPublicChange from '@/features/dashboard/bookingChanges/hooks/useRejectPublicChange.ts';
import { bookingSummaryApiQueryKeys } from '@/features/public/bookingSummary/api/bookingSummaryApi.queryKeys.ts';
import {
  PublicClientDto,
  PublicSummaryParticipantDto,
} from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import PendingChangeCard from './PendingChangeCard.tsx';
import RejectPendingChangeDialog from './RejectPendingChangeDialog.tsx';

interface PendingChangeSectionProps {
  orgSlug: string;
  token: string;
  participants: PublicSummaryParticipantDto[];
  client: PublicClientDto;
}

/**
 * Zmiany czekające na decyzję zapisującego. Panel powstaje tylko wtedy, gdy
 * paczka faktycznie czeka — brak paczki jest zwykłym stanem strony, więc nie ma
 * tu ani szkieletu, ani komunikatu o błędzie: podsumowanie ma być czytelne
 * także wtedy, gdy zmian nie ma.
 */
const PendingChangeSection = ({
  orgSlug,
  token,
  participants,
  client,
}: PendingChangeSectionProps) => {
  const queryClient = useQueryClient();
  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const { data: change, isError } = useGetPendingPublicChange(
    orgSlug,
    token,
    participants,
    client,
  );
  const accept = useAcceptPublicChange(orgSlug, token);
  const reject = useRejectPublicChange(orgSlug, token);

  /*
   * Nieudany odczyt też chowa panel. React Query trzyma przy błędzie ostatnie
   * udane dane, a paczka po decyzji klienta przestaje istnieć — bez tego panel
   * zostawałby na ekranie po akceptacji i zapraszał do drugiej.
   */
  if (!change || isError) {
    return null;
  }

  /*
   * Po decyzji odświeżamy całe podsumowanie, nie samą paczkę: akceptacja
   * przepisuje kwoty uczestników i harmonogram rat, a to one stoją na tej
   * stronie tuż pod panelem.
   */
  const refreshSummary = () =>
    queryClient.invalidateQueries({
      queryKey: bookingSummaryApiQueryKeys.summary(orgSlug, token),
    });

  return (
    <>
      <PendingChangeCard
        change={change}
        isDeciding={accept.isPending || reject.isPending}
        onAccept={() =>
          accept.mutate(
            {
              changeId: change.changeId,
              // Klient potwierdza kwotę, którą ma przed oczami — rozjazd z kwotą
              // serwera kończy się 409 i komunikatem z `useAcceptPublicChange`.
              request: { expectedAdditionalPayment: change.balance },
            },
            { onSuccess: refreshSummary },
          )
        }
        onReject={() => setRejectDialogOpen(true)}
      />
      {/* Odrzucenie jest stanem końcowym, a klient nie ma jak cofnąć własnego
        kliknięcia — dlatego przez okno, nie wprost z przycisku. */}
      {isRejectDialogOpen && (
        <RejectPendingChangeDialog
          isRejecting={reject.isPending}
          onClose={() => setRejectDialogOpen(false)}
          onReject={(form) =>
            reject.mutate(
              { changeId: change.changeId, form },
              {
                onSuccess: () => {
                  setRejectDialogOpen(false);
                  void refreshSummary();
                },
              },
            )
          }
        />
      )}
    </>
  );
};

export default PendingChangeSection;
